import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { api } from "@/lib/convexApi";
import { isCreditKeyPurchaserRole } from "@/lib/roles";
import { CreditKeyPanelBoundary } from "./CreditKeyPanelBoundary";

/** Load access details only when an administrator opens this account's control. */
export function CreditKeyUserAccessButton({ customerId, customerName, role }: {
  customerId: string;
  customerName: string;
  role: string;
}) {
  const identity = useAuthIdentity();
  const [open, setOpen] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const saving = React.useRef(false);
  const trigger = React.useRef<HTMLButtonElement>(null);
  if (!isCreditKeyPurchaserRole(role) || identity.userRole !== "admin" ||
      !identity.isAuthBridgeReady || !identity.isConvexAuthenticated) return null;

  return (
    <>
      <Button ref={trigger} type="button" variant="outline" className="mt-2 min-h-10 w-fit text-xs"
        aria-label={`Credit Key access for ${customerName}`} onClick={() => setOpen(true)}>
        Credit Key access
      </Button>
      <Dialog open={open} onOpenChange={(next) => { if (!saving.current) setOpen(next); }}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-md"
          showCloseButton={!pending}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => { if (saving.current) event.preventDefault(); }}
          onPointerDownOutside={(event) => { if (saving.current) event.preventDefault(); }}
          onCloseAutoFocus={(event) => { event.preventDefault(); trigger.current?.focus(); }}>
          <DialogHeader>
            <DialogTitle>Credit Key access</DialogTitle>
            <DialogDescription>{customerName}</DialogDescription>
          </DialogHeader>
          {open ? <CreditKeyPanelBoundary key={customerId}>
            <CreditKeyUserAccessEditor customerId={customerId} pending={pending}
              onPendingChange={(next) => { saving.current = next; setPending(next); }}
              onClose={() => setOpen(false)} />
          </CreditKeyPanelBoundary> : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

type AccessState = { enabled: boolean; paused: boolean; revision: number };

function CreditKeyUserAccessEditor({ customerId, ...props }: {
  customerId: string;
  pending: boolean;
  onPendingChange: (pending: boolean) => void;
  onClose: () => void;
}) {
  const access = useQuery(api.creditKey.getCustomerAccess, { customerId }) as AccessState | undefined;
  if (!access) return <p role="status" className="text-sm text-muted-foreground">Loading Credit Key access…</p>;
  return <CreditKeyUserAccessForm customerId={customerId} access={access} {...props} />;
}

function CreditKeyUserAccessForm({ customerId, access, pending, onPendingChange, onClose }: {
  customerId: string;
  access: AccessState;
  pending: boolean;
  onPendingChange: (pending: boolean) => void;
  onClose: () => void;
}) {
  const setAccess = useMutation(api.creditKey.setCustomerAccess);
  const [initialAccess] = React.useState(access);
  const [reason, setReason] = React.useState("");
  const [failed, setFailed] = React.useState(false);
  const inFlight = React.useRef(false);
  const cancel = React.useRef<HTMLButtonElement>(null);
  const id = React.useId();
  const stale = access.revision !== initialAccess.revision;
  const enabled = !initialAccess.enabled;
  React.useEffect(() => { cancel.current?.focus(); }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (inFlight.current || failed || stale || reason.trim().length > 500) return;
    inFlight.current = true;
    onPendingChange(true);
    try {
      await setAccess({ customerId, enabled, expectedRevision: initialAccess.revision, reason: reason.trim() || undefined });
      toast.success(enabled ? "Credit Key access enabled" : "Credit Key access removed");
      onClose();
    } catch {
      setFailed(true);
    } finally {
      inFlight.current = false;
      onPendingChange(false);
    }
  }

  return <form className="space-y-4" onSubmit={submit}>
    <p className="text-sm text-muted-foreground">
      {enabled ? "Let this account set up and pay with Credit Key." : "Remove Credit Key from this account's payment options. Existing orders stay available."}
    </p>
    {access.paused ? <p className="text-sm text-muted-foreground">New Credit Key financing is currently paused.</p> : null}
    <div className="space-y-2">
      <Label htmlFor={`${id}-reason`}>Reason (optional)</Label>
      <Textarea id={`${id}-reason`} value={reason} onChange={(event) => setReason(event.target.value)}
        rows={2} maxLength={500} disabled={pending || failed || stale} />
    </div>
    {failed || stale ? <p role="alert" className="text-sm text-destructive">
      {stale ? "Access changed while this dialog was open. Close and reopen it to review the current setting." : "Access could not be updated. Close and reopen this dialog to check the current setting."}
    </p> : null}
    <DialogFooter>
      <Button ref={cancel} type="button" variant="outline" className="min-h-10" disabled={pending} onClick={onClose}>Cancel</Button>
      <Button type="submit" variant={enabled ? "default" : "destructive"} className="min-h-10"
        disabled={pending || failed || stale || reason.trim().length > 500} aria-busy={pending}>
        {pending ? <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" /> : null}
        {pending ? "Saving…" : enabled ? "Enable access" : "Remove access"}
      </Button>
    </DialogFooter>
  </form>;
}
